import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@/lib/api-client';
import { Bonus } from '@/types/api/bonus';
import { Money } from '@/types/api/utils';

export enum BonusCodeKind {
  Standard = 'standard',
  /** A "Tipsa en kompis" code shared by another group's sale. */
  Referral = 'referral',
}

const invalidBonusCodeChars = (code: string) => [...new Set(code.match(/[^A-Za-z0-9-]/g) ?? [])];

export const BonusCodeSchema = z
  .string()
  .trim()
  .superRefine((code, ctx) => {
    const invalid = invalidBonusCodeChars(code);
    if (invalid.length) {
      const chars = invalid.map((c) => (c === ' ' ? 'mellanslag' : c)).join(', ');
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Bonuskoden kan inte innehålla ${chars}. Använd bara A–Z, 0–9 och bindestreck.`,
      });
    } else if (code.length < 3 || code.length > 32) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Bonuskoden ska vara 3–32 tecken lång' });
    }
  });

export type BonusCodeCheck = {
  code: string;
  valid: boolean;
  /** What a sale started with the code gets, if anything. */
  bonus: Bonus | null;
  /** Null when the code isn't valid. */
  kind: BonusCodeKind | null;
  /** For a referral code, what the sharing group earns once your sale reaches the bonus threshold. */
  commission: Money | null;
};

export const normalizeBonusCode = (code: string) => code.trim().toUpperCase();

export const checkBonusCode = async ({ code }: { code: string }): Promise<BonusCodeCheck> => {
  return (await api.get(`/bonus-codes/check`, { params: { code } })).data;
};

export const checkBonusCodeQueryOptions = (code: string) => {
  const normalized = normalizeBonusCode(code);

  return queryOptions({
    queryKey: ['bonus-codes', 'check', normalized],
    queryFn: () => checkBonusCode({ code: normalized }),
  });
};
